import { jsx } from "react/jsx-runtime";
import { useRef, useState, useMemo, useCallback } from "react";
import { useConfig } from "../../../../contexts/config-context.js";
import { useTranslation } from "../../../../contexts/translation-context.js";
import { useId } from "../../../../hooks/use-id.js";
import { CoreDropdownComponent } from "../../../autocomplete/wut/WixUiCoreComponents/components/CoreDropdown/CoreDropdown.js";
import { toLongCountryCode } from "../phone-input-utils.js";
import { CountryOption } from "./country-option.js";
import { st, classes } from "./dropdown-with-search.st.css";
import { FlagButton } from "./flag-button.js";
import { Search } from "./search.js";
import { OptionFactory } from "../../../autocomplete/wut/WixUiCoreComponents/components/CoreDropdownOption/OptionFactory.js";
const DropdownWithSearch = ({
  className,
  theme,
  countryCode,
  countries,
  onSelect,
  searchPhrase,
  onSearch,
  onKeyDown,
  readOnly,
  disabled,
  ariaLabel
}) => {
  const { isRTL: rtl } = useConfig();
  const longCountryCode = toLongCountryCode(countryCode);
  const flagButtonRef = useRef(null);
  const dropdownRef = useRef(null);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchStyle, setSearchStyle] = useState({});
  const { comboboxProps, listboxProps, setActiveDescendant } = useComboxbox();
  const options = useMemo(() => countries.map(createOption), [countries]);
  const initialSelectedIds = countryCode ? [countryCode] : [];
  const firstCountryCode = countries[0]?.shortCountryCode;
  const focusOnOption = searchPhrase ? firstCountryCode : countryCode ?? firstCountryCode;
  const handleKeydown = useCallback(
    (event) => {
      switch (event.key) {
        case "Escape":
          event.preventDefault();
          dropdownRef.current?.close();
          flagButtonRef.current?.focus();
          break;
        case "ArrowUp":
        case "ArrowDown":
        case "Enter":
        case "Tab":
          dropdownRef.current?.onKeyDown(event);
          break;
      }
      onKeyDown(event);
    },
    [onKeyDown]
  );
  const handleSelect = useCallback(
    (option) => {
      const _countryCode = option?.id;
      onSelect(_countryCode);
      setIsOpen(false);
    },
    [onSelect]
  );
  const handleSearch = useCallback(
    (event) => onSearch(event.target.value),
    [onSearch]
  );
  const handleExpandChange = useCallback(
    (open) => {
      setIsOpen(open);
      onSearch("");
    },
    [onSearch]
  );
  const handleSearchMounted = useCallback((input) => {
    const width = input?.getBoundingClientRect()?.width;
    width && setSearchStyle({ minWidth: width });
  }, []);
  const handleOptionHover = useCallback(
    (option) => {
      option && setActiveDescendant(option._DOMid);
    },
    [setActiveDescendant]
  );
  return /* @__PURE__ */ jsx(
    CoreDropdownComponent,
    {
      appendTo: "window",
      readOnly,
      disabled,
      contentId: listboxProps.id,
      contentRole: listboxProps.role,
      className: st(classes.dropdown, { rtl }),
      contentClassName: st(classes.dropdownContainer, {
        rtl,
        theme
      }),
      ref: dropdownRef,
      initialSelectedIds,
      options,
      placement: rtl ? "bottom-end" : "bottom-start",
      openTrigger: "click",
      onSelect: handleSelect,
      onExpandedChange: handleExpandChange,
      onOptionHover: handleOptionHover,
      focusOnOption,
      eventsEnabled: false,
      noResultsText: t("field.phone.country-selector-dropdown.no-result"),
      fixedHeader: /* @__PURE__ */ jsx(
        "div",
        {
          style: searchStyle,
          ref: handleSearchMounted,
          className: classes.searchContainer,
          children: /* @__PURE__ */ jsx(
            Search,
            {
              ...comboboxProps,
              className,
              theme,
              autoFocus: true,
              value: searchPhrase,
              onChange: handleSearch,
              onKeyDown: handleKeydown
            }
          )
        }
      ),
      children: /* @__PURE__ */ jsx(
        FlagButton,
        {
          readOnly,
          ref: flagButtonRef,
          arrowDirection: isOpen ? "up" : "down",
          longCountryCode,
          ariaLabel
        }
      )
    }
  );
};
function createOption({
  longCountryCode,
  shortCountryCode,
  callingCode,
  countryName
}) {
  return OptionFactory.create({
    id: shortCountryCode,
    render: () => /* @__PURE__ */ jsx(
      CountryOption,
      {
        callingCode,
        longCountryCode,
        countryName
      }
    )
  });
}
function useComboxbox() {
  const listboxId = useId();
  const [activeDescendant, setActiveDescendant] = useState();
  const comboboxProps = {
    role: "combobox",
    "aria-autocomplete": "list",
    "aria-haspopup": "listbox",
    "aria-expanded": true,
    "aria-owns": listboxId,
    "aria-activedescendant": activeDescendant
  };
  const listboxProps = {
    role: "listbox",
    id: listboxId
  };
  return { comboboxProps, listboxProps, setActiveDescendant };
}
export {
  DropdownWithSearch
};
//# sourceMappingURL=dropdown-with-search.js.map
